import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock, Mail, Pencil, Phone, Trash2, UserRound, Users, type LucideIcon } from "lucide-react";
import { useAuth } from "@/app/auth";
import { useCan, useMenuAccess } from "@/app/access";
import { useCustomer, useCustomerRequirements, useDeleteCustomer } from "@/api/hooks";
import { CustomerRequirementsEditor, requirementsFromDto } from "@/components/customers/CustomerRequirementsEditor";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { PageBody } from "@/components/ui/PageHeader";
import { Panel } from "@/components/ui/Panel";
import { Badge } from "@/components/ui/Badge";
import { Button, buttonStyles } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { CurrencyAmountList } from "@/components/domain/CurrencyAmountList";
import { formatDate } from "@/domain/datetime";
import { CustomerDocuments } from "@/components/customers/CustomerDocuments";

function Info({ label, children, icon: Icon }: { label: string; children: React.ReactNode; icon?: LucideIcon }) {
  return <div className="min-w-0">
    <dt className="flex items-center gap-1.5 text-xs text-ink-soft">
      {Icon && <Icon className="size-3.5 shrink-0" aria-hidden="true" />}
      {label}
    </dt>
    <dd className="mt-1 break-words text-sm text-ink">{children || "—"}</dd>
  </div>;
}

export function CustomerDetailPage() {
  const { id } = useParams();
  return <CustomerDetail key={id} id={id} />;
}

function CustomerDetail({ id }: { id?: string }) {
  const { t } = useTranslation();
  // Điều khoản thanh toán + yêu cầu sản phẩm / bao bì: ô Xem "Điều khoản & yêu cầu đặt hàng" (mục 8, 04/11/2026).
  const seesTerms = useMenuAccess("customerTerms")("view");
  const { search, state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = useCan();
  const { data: customer, isLoading, error, refetch } = useCustomer(id);
  const del = useDeleteCustomer();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const listTo = `/customers${search}`;
  const saved = typeof state === "object" && state !== null && "customerSaved" in state && state.customerSaved === true;
  usePageCrumbs(customer ? [{ label: customer.companyName }] : [{ label: t(error ? "customers.errorLoad" : "common.loading") }], listTo);
  if (isLoading) return <PageBody><LoadingState label={t("customers.loading")} /></PageBody>;
  if (error || !customer) {
    const missing = error instanceof ApiClientError && error.status === 404;
    return <PageBody><ErrorState message={t(missing ? "customers.notFound" : "customers.errorLoad")}
      onRetry={missing ? undefined : () => void refetch()} /></PageBody>;
  }
  const canEdit = user ? can("records:update") : false;
  const canDelete = user ? can("records:delete") : false;
  const contacts = [...customer.contacts].sort((a, b) => a.sortOrder - b.sortOrder);
  const deleteCustomer = async () => {
    setDeleteError(null);
    try {
      await del.mutateAsync(customer.id);
      navigate(listTo);
    } catch (err) {
      setConfirmDelete(false);
      setDeleteError(getLocalizedError(err, t, "customers.errorDelete"));
    }
  };
  return <PageBody className="space-y-4">
    <ConfirmDialog open={confirmDelete} title={customer.companyName}
      description={t("customers.confirmDeleteNamed", { name: customer.companyName })}
      confirmLabel={t("common.delete")} cancelLabel={t("common.cancel")}
      busy={del.isPending} onCancel={() => setConfirmDelete(false)} onConfirm={() => void deleteCustomer()} />
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Link to={listTo} className={buttonStyles({ variant: "ghost", size: "sm" })}>
        <ArrowLeft className="size-4" /> {t("customers.backToList")}
      </Link>
      <div className="flex items-center gap-2">
        {canEdit && <Link to={`/customers/${customer.id}/edit${search}`} className={buttonStyles({ variant: "secondary", size: "sm" })}>
          <Pencil className="size-4" /> {t("common.edit")}
        </Link>}
        {canDelete && <Button variant="danger" size="icon-sm" disabled={del.isPending}
          aria-label={t("common.delete")} onClick={() => setConfirmDelete(true)}>
          <Trash2 className="size-4" />
        </Button>}
      </div>
    </div>
    {saved && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{t("common.saved")}</p>}
    {deleteError && <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-sm text-danger">{deleteError}</p>}
    <Panel className="p-5">
      <div className="flex items-start gap-4">
        <span className="grid size-12 shrink-0 place-items-center rounded-md bg-brand-tint text-brand-ink"><Users className="size-6" aria-hidden="true" /></span>
        <div className="min-w-0">
          <h1 className="break-words text-2xl font-semibold tracking-tight text-ink">
            {customer.code ? <span className="mr-2 rounded-xs bg-sunken px-1.5 align-middle font-mono text-base font-semibold tabular text-ink" title={t("customers.code")}>{customer.code}</span> : null}
            {customer.companyName}
          </h1>
          <p className="mt-1 break-words text-sm text-ink-soft">{customer.address || "—"}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2"><Badge tone={customer.classification === "china" ? "info" : "neutral"} outline>
            {t(customer.classification === "china" ? "customers.china" : "customers.vietnam")}
          </Badge>
          {/* Khách riêng tư (30/09/2026): chỉ quản trị viên nhận được khách này, nên chỉ họ thấy nhãn. */}
          {customer.isPrivate ? (
            <Badge tone="neutral" title={t("customers.privateHint")}>
              <Lock className="size-3" aria-hidden="true" /> {t("customers.private")}
            </Badge>
          ) : null}</div>
        </div>
      </div>
      <dl className="mt-5 grid grid-cols-1 gap-4 border-t border-line pt-4 sm:grid-cols-3">
        <Info label={t("common.phone")} icon={Phone}>{customer.phone && <a href={`tel:${customer.phone}`} className="font-mono tabular text-brand hover:underline">{customer.phone}</a>}</Info>
        <Info label={t("customers.openAr")}><CurrencyAmountList items={customer.openArByCurrency} /></Info>
        <Info label={t("products.created")}><span className="font-mono tabular">{formatDate(customer.createdAt)}</span>{customer.createdByName && <p className="mt-0.5 text-xs text-ink-soft">{customer.createdByName}</p>}</Info>
      </dl>
    </Panel>
    <CustomerDocuments customerId={customer.id} />
    <Panel className="p-5">
      <h2 className="text-lg font-semibold text-ink">{t("customers.sectionSales")}</h2>
      <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {seesTerms && <Info label={t("customers.terms")}>{customer.paymentTerm}</Info>}
        <Info label={t("customers.cs")}>
          {customer.followers.length ? (
            <ul className="space-y-0.5">
              {customer.followers.map((f) => (
                <li key={f.id}>
                  {f.name}
                  {f.active ? null : <span className="ml-1 text-xs text-ink-soft">({t("customers.followerDisabled")})</span>}
                </li>
              ))}
            </ul>
          ) : (
            <>
              {t("customers.unassigned")}
              <p className="mt-1 text-xs text-ink-soft">{t("customers.followersNone")}</p>
            </>
          )}
        </Info>
        <Info label={t("lot.pProcessDefault")}>
          {t(customer.pProcessDefault ? "lot.pProcessDefaultOn" : "lot.pProcessDefaultOff")}
          <p className="mt-1 text-xs text-ink-soft">{t("lot.pProcessDefaultHint")}</p>
        </Info>
      </dl>
    </Panel>
    <Panel className="p-5">
      <h2 className="text-lg font-semibold text-ink">{t("customers.sectionContacts")} <span className="font-mono tabular text-sm text-ink-soft">({contacts.length})</span></h2>
      {contacts.length === 0 ? <p className="mt-3 text-sm text-ink-soft">{t("customers.noContacts")}</p> :
        <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
          {contacts.map((contact) => <section key={contact.id} className="min-w-0 rounded-md border border-line bg-paper p-4">
            <h3 className="flex items-start gap-2 break-words text-base font-semibold text-ink">
              <UserRound className="mt-0.5 size-4 shrink-0 text-ink-soft" aria-hidden="true" />
              <span className="min-w-0">{contact.name}</span>
              {contact.isPrivate ? (
                <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-medium text-ink-soft">
                  <Lock className="size-3" aria-hidden="true" /> {t("customers.contactPrivate")}
                </span>
              ) : null}
            </h3>
            {/* Ba ô chính luôn hiện; bốn ô ít dùng (điện thoại 2 6,5%, thương hiệu 5,8%, WeChat và nhóm 0% trên production 19/09/2026)
                chỉ hiện khi có dữ liệu — trước đây thẻ nào cũng kèm ba bốn dòng "—". */}
            <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Info label={t("customers.phone1")} icon={Phone}>{contact.phone1 && <a href={`tel:${contact.phone1}`} className="font-mono tabular text-brand hover:underline">{contact.phone1}</a>}</Info>
              <Info label={t("common.email")} icon={Mail}>{contact.email && <a href={`mailto:${contact.email}`} className="break-all text-brand hover:underline">{contact.email}</a>}</Info>
              <Info label={t("customers.section")}>{contact.section}</Info>
              {contact.phone2 && <Info label={t("customers.phone2")} icon={Phone}><a href={`tel:${contact.phone2}`} className="font-mono tabular text-brand hover:underline">{contact.phone2}</a></Info>}
              {contact.brandName && <Info label={t("customers.brandName")}>{contact.brandName}</Info>}
              {contact.wechat && <Info label={t("customers.wechat")}>{contact.wechat}</Info>}
              {contact.team && <Info label={t("customers.team")}>{contact.team}</Info>}
            </dl>
          </section>)}
        </div>}
    </Panel>
    {/* Yêu cầu sản phẩm / bao bì (v1.8 mục 2.3): ở CUỐI hồ sơ khách như khách yêu cầu. */}
    {seesTerms && <CustomerRequirementsPanel customerId={customer.id} />}
  </PageBody>;
}

/** Yêu cầu sản phẩm / bao bì của khách (v1.8 mục 2.3) — hiện mỗi lần mở khách; sửa ở trang Sửa khách. */
function CustomerRequirementsPanel({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const q = useCustomerRequirements(customerId);
  return (
    <Panel className="p-5">
      <h2 className="text-lg font-semibold text-ink">{t("evidence.reqTitle")}</h2>
      <p className="mb-3 mt-0.5 text-xs text-ink-soft">{t("evidence.reqHint")}</p>
      {q.isLoading ? (
        <LoadingState />
      ) : q.isError ? (
        <ErrorState message={t("evidence.reqListLoadFailed")} onRetry={() => void q.refetch()} />
      ) : (
        <CustomerRequirementsEditor value={requirementsFromDto(q.data ?? [])} readOnly />
      )}
    </Panel>
  );
}
